import HTTP from 'Utils/HTTP';
import Validator from 'Utils/Validator';
import flatpickr from 'flatpickr';
import { German } from 'flatpickr/dist/l10n/de.js';

export class Form {
	constructor(el) {
		this.element = el;
		this.submitButton = el.querySelector('.js-submit-form');
		this.fieldset = el.querySelector('.js-form-fieldset');
		this.inputElements = Array.from(el.querySelectorAll('[data-input-inspect]'));
		this.calPickerElements = Array.from(el.querySelectorAll('.js-form-cal'));
		this.element.dataset.formId = btoa(Math.random()).substring(0, 12);
		window[this.element.dataset.formId] = this;

		if (this.element.dataset.preventSubmit != null) {
			this.element.addEventListener('submit', e => e.preventDefault());
		}

		this.changeEvent = new CustomEvent('change', { detail: { autoTrigger: true } });

		this.initEvents();
		this.validateInputs();
		setTimeout(() => {
			this.checkPrefilledLabelCheck();
		}, 100);
	}

	initEvents() {
		this.inputElements.forEach(input => {
			const { type } = input;

			const validateEventByInput = ['text', 'email', 'password', 'textarea'];
			const validateEventByChange = ['select-one', 'radio', 'checkbox'];

			if (validateEventByInput.includes(type)) {
				input.addEventListener('input', this.onBlur);
			}

			if (validateEventByChange.includes(type)) {
				input.addEventListener('change', this.onBlur);
			}

			input.addEventListener('blur', this.onBlur);
			input.addEventListener('focus', this.onFocus);

			if (type === 'select-one' || type === 'checkbox' || (type === 'radio' && input.checked)) {
				input.dispatchEvent(this.changeEvent);
			}
		});

		this.calPickerElements.forEach(input => {
			const { calType = 'default' } = input.dataset;
			const entryOptions = {
				past: {
					dateFormat: 'j. F Y',
					maxDate: new Date(),
					locale: German,
				},
				default: {
					dateFormat: 'j. F Y',
					locale: German,
				},
			};
			flatpickr(input, entryOptions[calType]);
		});

		const charCounterInputs = Array.from(this.element.querySelectorAll('[data-charcounter="1"]'));
		charCounterInputs.forEach(input => {
			const maxCount = +input.getAttribute('maxlength');
			input.addEventListener('input', e => {
				const charCount = input.value.length;
				const newCount = maxCount - charCount;

				const newCharMessage = this.getCharMessage(e.target, newCount);
				input.parentNode.dataset.charMessage = newCharMessage;
			});

			const newCharMessage = this.getCharMessage(input, maxCount);
			input.parentNode.dataset.charMessage = newCharMessage;
		});

		this.element.addEventListener('submit', this.onSubmit);

		Array.from(this.element.querySelectorAll('[data-toggle-name]')).forEach(input => {
			const { toggleInvert } = input.dataset;
			if (toggleInvert != null) {
				input.classList.remove('hidden');
			} else {
				input.classList.add('hidden');
			}
		});

		Array.from(this.element.querySelectorAll('[data-toggle-target]')).forEach(input =>
			input.addEventListener('change', this.onToggleChange)
		);

		this.togglePassword = ::this.togglePassword;
		Array.from(this.element.querySelectorAll('.js-form-password-reveal')).forEach(btn => {
			btn.addEventListener('click', this.togglePassword);
		});
	}

	checkPrefilledLabelCheck() {
		this.inputElements.forEach(element => {
			if (['text', 'email', 'password'].includes(element.type)) {
				if (element.value !== '') {
					element.dataset.isEmpty = 0;
				}
			}
		});
	}

	getCharMessage(input, count) {
		const messageText = input.parentNode.dataset.charMessageText;

		if (messageText && messageText.includes('*')) {
			const messageTextSplit = messageText.split('*');
			const newCount = messageTextSplit[0] + count + messageTextSplit[1];

			return newCount;
		}

		return count;
	}

	togglePassword(e) {
		const target = e.currentTarget;
		const inputTargetId = target.dataset.target;
		if (!inputTargetId) return;
		const inputTarget = document.getElementById(inputTargetId);
		if (!inputTarget) return;

		if (inputTarget.dataset.passwordVisible === '1') {
			target.classList.remove('is-visible');
			inputTarget.setAttribute('type', 'password');
			inputTarget.dataset.passwordVisible = 0;
		} else {
			target.classList.add('is-visible');
			inputTarget.setAttribute('type', 'text');
			inputTarget.dataset.passwordVisible = 1;
		}
	}

	onToggleChange = ({ currentTarget: target }) => {
		const { toggleTarget, toggleInvertState = false } = target.dataset;
		const state = target.checked && !toggleInvertState;

		const targetElements = Array.from(
			this.element.querySelectorAll(`[data-toggle-name="${toggleTarget}"`)
		);

		targetElements.forEach(targetElement => {
			const { toggleInvert } = targetElement.dataset;

			if ((state && toggleInvert == null) || (!state && toggleInvert != null)) {
				targetElement.classList.remove('hidden');
			} else {
				targetElement.classList.add('hidden');
			}
		});
	};

	onSubmit = e => {
		const formIsValid = this.validateInputs({ ignoreDirty: true });

		const captchaInput = this.element.querySelector('.js-form-captcha');

		if (window.reCaptchaSiteKey && captchaInput && window.grecaptcha) {
			captchaInput.value = window.grecaptcha.getResponse();

			if (captchaInput.value === '') {
				e.preventDefault();
				return false;
			}
		}

		if (!formIsValid) {
			e.preventDefault();
			return false;
		}

		if (this.submitButton.dataset.preventSubmit) {
			e.preventDefault();
			return false;
		}

		if (this.submitButton.dataset.disableOnSubmit) {
			if (this.submitButton.dataset.disableOnSubmitLabel) {
				this.submitButton.dataset.initialLabel = this.submitButton.innerHTML;
				this.submitButton.innerHTML = this.submitButton.dataset.disableOnSubmitLabel;
			}

			this.submitButton.dataset.preventSubmit = 1;

			setTimeout(() => {
				if (this.fieldset) {
					this.fieldset.setAttribute('disabled', 1);
				}
				this.submitButton.removeAttribute('data-prevent-submit', 1);
				this.submitButton.setAttribute('disabled', 1);
			}, 50);
		}

		if (this.element.dataset.submitAsAjax) {
			e.preventDefault();
			this.submitViaAjax();

			return false;
		}

		return true;
	};

	submitViaAjax = () => {
		const formData = new FormData();

		const captchaInput = this.element.querySelector('.js-form-captcha');

		if (window.reCaptchaSiteKey && captchaInput && window.grecaptcha) {
			captchaInput.value = window.grecaptcha.getResponse();

			if (captchaInput.value === '') {
				return;
			}
		}

		const inputs = Array.from(
			this.element.querySelectorAll('[data-ajax-submit]:not([data-ajax-data])')
		);
		inputs.forEach(input => formData.append(input.name, input.value));

		const ajaxDataInputs = Array.from(this.element.querySelectorAll('[data-ajax-data]'));

		let dataName = 'data';
		const dataSubmitNameInput = this.element.querySelector('[data-ajax-data-submit-name]');
		if (dataSubmitNameInput) {
			dataName = dataSubmitNameInput.name;
		}

		const data = ajaxDataInputs.reduce((prev, cur) => {
			if (cur.type === 'checkbox') {
				if (cur.dataset.validateAs === 'checkboxGroup') {
					const checkboxes = Array.from(this.element.querySelectorAll(`[name="${cur.name}"]`));
					prev[cur.name] = checkboxes
						.reduce((prev, cur) => {
							if (cur.checked) {
								prev.push(cur.value);
							}
							return prev;
						}, [])
						.join(',');
				} else {
					prev[cur.name] = cur.checked ? cur.value : 0;
				}
			} else {
				prev[cur.name] = cur.value;
			}
			return prev;
		}, {});

		formData.append(dataName, JSON.stringify(data));

		const errorMessage = this.element.querySelector('.js-form-error-message');
		const errorMessageResponse = this.element.querySelector('.js-form-error-message-response');
		const successMessage = this.element.querySelector('.js-form-success-message');
		const successMessageResponse = this.element.querySelector('.js-form-success-message-response');

		errorMessage && errorMessage.classList.add('hidden');
		errorMessage && successMessage.classList.add('hidden');

		let uri = this.element.getAttribute('action');
		if (this.element.dataset.submitUri) {
			uri = this.element.dataset.submitUri;
		}

		HTTP.post(uri, formData)
			.then(res => {
				const { success = false, message = '' } = res;
				window.grecaptcha?.reset();

				if (success) {
					if (this.submitButton.dataset.redirectAfterSubmit === '1') {
						this.element.removeAttribute('data-submit-as-ajax');
						this.element.submit();
					} else {
						errorMessage && errorMessage.classList.add('hidden');
						errorMessage && successMessage.classList.remove('hidden');
						if (message && successMessageResponse) {
							successMessageResponse.innerHTML = message;
						}
						this.submitButton.disabled = false;

						this.element.reset();
					}
				} else {
					successMessage && successMessage.classList.add('hidden');
					errorMessage && errorMessage.classList.remove('hidden');
					if (message && errorMessageResponse) {
						errorMessageResponse.innerHTML = message;
					} else if (errorMessageResponse) {
						errorMessageResponse.innerHTML = errorMessageResponse.dataset.defaultMessage;
					}
					this.submitButton.disabled = false;
				}

				this.fieldset && this.fieldset.removeAttribute('disabled');
			})
			.catch(error => {
				window.grecaptcha?.reset();
				successMessage && successMessage.classList.add('hidden');
				errorMessage && errorMessage.classList.remove('hidden');
				if (errorMessageResponse) {
					errorMessageResponse.innerHTML = errorMessageResponse.dataset.defaultMessage;
				}
				this.fieldset && this.fieldset.removeAttribute('disabled');
				this.submitButton.disabled = false;
			});
	};

	onBlur = e => {
		if (e && e.target && e.target.dataset.transformOnBlur !== undefined) {
			const transforms = e.target.dataset.transformOnBlur.split(',');
			transforms.forEach(transform => this.transformInputValue(e.target, transform));
		}

		if (e.target && e.target.value != null) {
			if (e.target.value === '' && e.target.dataset.alwaysEmpty == null) {
				e.target.dataset.isEmpty = 1;
			} else {
				e.target.dataset.isEmpty = 0;
			}
		}

		this.validateInputs({ blur: e.type === 'blur', target: e?.target ?? e?.currentTarget });
	};

	transformInputValue(input, transform) {
		switch (transform) {
			case 'removeSpaces': {
				input.value = input.value.replace(/\s/gm, '');
				break;
			}
			case 'uppercase': {
				input.value = input.value.toUpperCase();
				break;
			}
			case 'number': {
				let value = +input.value.trim();
				const max = parseInt(input.getAttribute('max'));
				const min = parseInt(input.getAttribute('min'));

				if (!isNaN(max)) {
					value = Math.min(max, value);
				}

				if (!isNaN(min)) {
					value = Math.max(min, value);
				}
				input.value = value;
				break;
			}
		}
	}

	onFocus = ({ currentTarget: target }) => {
		target.dataset.dirty = 1;
	};

	validateInputs({ showError = true, blur = false, target = null, ignoreDirty = false } = {}) {
		const requiredInputFields = Array.from(
			this.element.querySelectorAll(
				'[data-input-inspect][required],[data-input-inspect][data-dirty],[data-input-inspect][data-always-dirty]'
			)
		);

		let formIsValid = true;
		for (let i = 0; i < requiredInputFields.length; i++) {
			const input = requiredInputFields[i];
			const { validateOnlyBlur = false, validateServiceCallback } = input.dataset || {};

			if (
				(validateOnlyBlur !== false && !blur) ||
				(blur && input !== target && validateOnlyBlur !== false)
			)
				continue;

			let validateResponse = Validator.validate(input, { ignoreDirty, form: this.element });
			if (validateResponse.service) {
				validateResponse.service.then(res => {
					if (validateServiceCallback) {
						Forms.invokeCallback(validateServiceCallback, res);
					}
				});
			}

			if (validateResponse.custom) {
				const customResponse = Forms.invokeCallback(
					`validateCustom${validateResponse.custom}`,
					input
				);
				if (customResponse) {
					validateResponse = customResponse;
				}
			}

			if (showError) {
				if (!validateResponse.error) {
					if (input.parentNode.classList.contains('is-invalid') && validateResponse.isDirty) {
						input.parentNode.classList.remove('is-invalid');
					}
				} else {
					input.parentNode.classList.add('is-invalid');
				}
			}

			formIsValid = validateResponse.isValid && formIsValid;
		}
		if (
			this.submitButton &&
			this.submitButton.dataset.ignoreValidation !== 'true' &&
			this.submitButton.dataset.disableIfInvalid === '1'
		) {
			this.submitButton.disabled = !formIsValid;
		}

		return formIsValid;
	}

	enableForm() {
		if (this.submitButton) {
			const { initialLabel = '' } = this.submitButton.dataset;
			if (initialLabel !== '') {
				this.submitButton.innerHTML = this.submitButton.dataset.initialLabel;
			}
		}
		this.fieldset && this.fieldset.removeAttribute('disabled');
	}

	resetForm() {
		this.enableForm();
		this.element.reset();
		Array.from(this.element.querySelectorAll('[data-dirty]')).forEach(input =>
			input.removeAttribute('data-dirty')
		);
		this.validateInputs({ showError: false });
	}

	fillForm(data) {
		for (const inputName in data) {
			const value = data[inputName];
			const el = this.element.querySelector(`[name="${inputName}"]`);
			if (el) {
				switch (el.type) {
					case 'select-one': {
						el.value = value;
						break;
					}
					case 'email':
					case 'text': {
						el.value = value;
						break;
					}
				}
			}
		}
	}
}

export default class Forms {
	static addCallback(key, func) {
		if (!window.SLIM_FORM_CALLBACKS) window.SLIM_FORM_CALLBACKS = {};

		if (window.SLIM_FORM_CALLBACKS[key]) console.error('Callback key already set', key);

		window.SLIM_FORM_CALLBACKS[key] = func;
	}

	static invokeCallback(key, payload) {
		if (window.SLIM_FORM_CALLBACKS && window.SLIM_FORM_CALLBACKS[key])
			return window.SLIM_FORM_CALLBACKS[key](payload);
	}
	constructor() {
		const loginForms = Array.from(document.querySelectorAll('.js-form'));
		loginForms.forEach(element => new Form(element));
	}
}
